import { isStaging } from "config";
import {
  getDisconnectedCookie,
  setDisconnectedCookie,
  deleteDisconnectedCookie,
} from "utils/storage";
import { Connector, ConnectorDispatch, MetaMaskProvider } from "../types";
import { getInjectedProvider } from "./getInjectedProvider";

/**
 * Convert chainId returned from MetaMask events/requests
 */
const parseChainId = (chainId: string) => Number.parseInt(chainId, 16);

class MetaMaskConnector extends Connector {
  public provider: MetaMaskProvider | undefined;

  /**
   * Store any in-progress immediate connection to use if manual connection is attempted
   */
  private immediateConnection?: Promise<void>;

  constructor(dispatch: ConnectorDispatch) {
    super(dispatch);

    // Pretend we don't have a connection if user attempted to disconnect
    if (!getDisconnectedCookie(this.constructor.name)) {
      this.connectImmediately();
    }
  }

  /**
   * Set up event handlers on provider object
   */
  private async initialize(): Promise<void> {
    // If called by connect() while connectImmediately() is still running, don't overwrite original promise
    if (this.immediateConnection) return this.immediateConnection;

    // Store connection promise in case of above edge case
    await (this.immediateConnection = getInjectedProvider().then((provider) => {
      if (provider) {
        this.provider = provider as MetaMaskProvider;

        // If metamask and coinbase wallet are both injected, extract metamask from providers array
        if (this.provider.providers?.length) {
          this.provider =
            this.provider.providers.find((p) => p.isMetaMask) ??
            this.provider.providers[0];
        }

        // Update AuthContext state on any connection change events
        this.provider.on("connect", ({ chainId }): void => {
          this.dispatch({ type: "update", chainId: parseChainId(chainId) });
        });

        this.provider.on("disconnect", (error): void => {
          this.dispatch({ type: "error", error });
        });

        this.provider.on("chainChanged", (chainId: string): void => {
          this.dispatch({ type: "update", chainId: parseChainId(chainId) });
        });

        this.provider.on("accountsChanged", (accounts: string[]): void => {
          if (accounts.length === 0) {
            this.dispatch({ type: "error", error: undefined });
          } else {
            this.dispatch({ type: "update", accounts });
          }
        });
      }
    }));

    return undefined;
  }

  public async connectImmediately(): Promise<void> {
    await this.initialize();

    if (!this.provider) return this.dispatch({ type: "reset" });

    return Promise.all([
      this.provider.request({ method: "eth_chainId" }) as Promise<string>,
      // NOTE: use eth_accounts instead of eth_requestAccounts assuming we have permisions, if we don't
      // then we can't reconnect the user since they haven't connected yet
      this.provider.request({ method: "eth_accounts" }) as Promise<string[]>,
    ])
      .then(([chainId, accounts]) => {
        if (accounts.length) {
          this.dispatch({
            type: "update",
            chainId: parseChainId(chainId),
            accounts,
          });
        } else {
          throw new Error("No accounts from MetaMask provider");
        }
      })
      .catch(() => {
        // Fail silently, most commonly because user hasn't connected yet
        // console.debug("Couldn't connect immediately", error);
        this.dispatch({ type: "reset" });
      });
  }

  public async connect(): Promise<void> {
    if (!this.provider?.isConnected?.())
      this.dispatch({ type: "startConnecting" });

    await this.initialize();

    if (!this.provider) {
      // NOTE: this deep link will open the metamask mobile app if the user is on a regular
      // mobile browser. It can only point toward generated deep links, so check if environment
      // is production or staging; we can't redirect in dev environment
      window.location.href = isStaging
        ? "https://metamask.app.link/dapp/thesecrethug.xyz/sign-in"
        : "https://metamask.app.link/dapp/thehug.xyz/sign-in";

      return this.dispatch({ type: "error", error: new Error("No MetaMask") });
    }

    return Promise.all([
      this.provider.request({ method: "eth_chainId" }) as Promise<string>,
      // NOTE: the extra request for wallet permissions is to allow users to "reconnect" if they've
      // previously disconnected. It should open the extension and allow them to select a different
      // wallet address, although the original would still be connected in the extension. See
      // this.disconnect() for more info.
      this.provider
        .request({
          method: "wallet_requestPermissions",
          params: [{ eth_accounts: {} }],
        })
        .then(
          () => this.provider?.request({ method: "eth_requestAccounts" }),
          (error) => {
            // Method wallet_requestPermissions doesn't exist on Metamask mobile app...
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((error as any).code === -32601) {
              return this.provider?.request({ method: "eth_requestAccounts" });
            }
            throw error;
          }
        ) as Promise<string[]>,
    ])
      .then(([chainId, accounts]) => {
        // Once manual connect is successful, ensure previous disconnect attempt is removed to allow reconnect
        deleteDisconnectedCookie(this.constructor.name);

        return this.dispatch({
          type: "update",
          chainId: parseChainId(chainId),
          accounts,
        });
      })
      .catch((error) => {
        this.dispatch({ type: "error", error });
      });
  }

  public disconnect(): void {
    setDisconnectedCookie(this.constructor.name);
    this.dispatch({ type: "error", error: undefined });
  }
}

export { MetaMaskConnector };
